/**
 * Certification Uploader Component
 * Handles image/PDF upload and camera capture for certification documents.
 * Adapted from ToolPhotoUploader -- no AI analysis.
 *
 * P6-B1 — storage signed-URL hot-spot fix (2026-05-06).
 *
 * The `passport-certifications` Supabase Storage bucket is PRIVATE
 * (`storage.buckets.public = false` per migration 20260301052017).
 * Pre-B1 this component shipped a function literally named
 * `getSignedUrl` whose body called `supabase.storage.from(...)
 * .getPublicUrl(...)` and returned `data.publicUrl`. That misleading
 * name + wrong call path produced URLs that are NOT publicly fetchable
 * (the bucket is private, so `<img src={publicUrl}>` rendered broken
 * thumbnails for anyone whose RLS-bound cookies didn't line up with
 * the legacy public-URL behavior). The fix routes every image through
 * the canonical batched `createSignedUrls(paths, 3600)` pattern from
 * `src/hooks/useDviMedia.ts:47-49` (the proven inline-image signed-URL
 * pattern in the codebase) and wraps the resolution in `useQuery` so:
 *   1. Signed URLs are fetched ONCE per (cert + paths) tuple, not once
 *      per render — no infinite loops, no `useEffect+setState` pitfalls.
 *   2. Cached at the queryClient with a 5-minute `staleTime` (URLs are
 *      valid for 1h; mid-session refetches are unnecessary).
 *   3. The returned `Map<storage_path, signedUrl>` allows synchronous
 *      lookups in JSX, so each `<img>` keeps its existing render shape.
 *   4. While URLs are loading, `<img src={undefined}>` renders nothing
 *      — same as the pre-B1 broken state, so loading UX is unchanged.
 *
 * The 3600-second expiry matches the canonical batch-signed-URL pattern
 * used by `useDviMedia.ts`, `useToolbox.ts:150`, `artifactUpload.ts`,
 * and the parking-lot views. `LegalDocumentsPanel.tsx:41` uses 60s for
 * legal-doc preview only — that's the documented exception, not the
 * default.
 */

import { useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Camera, ImagePlus, FileText, X, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useUploadCertificationMedia, useDeleteCertificationMedia, useCertificationMedia } from '@/hooks/usePassportSections';
import { supabase } from '@/integrations/supabase/client';

interface CertificationUploaderProps {
  certificationId: string;
  userId: string;
  disabled?: boolean;
  className?: string;
}

export function CertificationUploader({
  certificationId,
  userId,
  disabled,
  className,
}: CertificationUploaderProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const { data: mediaItems, isLoading } = useCertificationMedia(certificationId);
  const uploadMedia = useUploadCertificationMedia();
  const deleteMedia = useDeleteCertificationMedia();

  const isUploading = uploadMedia.isPending;

  const handleFileSelect = async (file: File) => {
    if (!file.type.startsWith('image/') && file.type !== 'application/pdf') return;
    uploadMedia.mutate({ file, certId: certificationId, userId });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelect(file);
    e.target.value = '';
  };

  const handleRemove = (id: string, storagePath: string) => {
    deleteMedia.mutate({ id, storagePath, certId: certificationId, userId });
  };

  // P6-B1: batch-resolve signed URLs for every certification media item
  // in one round-trip via `createSignedUrls`. Keyed off the certificationId
  // + the actual storage paths so the cache hits across renders that don't
  // change the underlying media list. The 3600-second expiry mirrors the
  // canonical batch-signed-URL pattern used by `useDviMedia` / `useToolbox`.
  const storagePaths = useMemo(
    () => (mediaItems ?? []).map((item) => item.storage_path),
    [mediaItems],
  );
  const { data: signedUrlMap } = useQuery({
    queryKey: ['certification-media-signed-urls', certificationId, storagePaths],
    queryFn: async () => {
      if (storagePaths.length === 0) return new Map<string, string>();
      const { data, error } = await supabase.storage
        .from('passport-certifications')
        .createSignedUrls(storagePaths, 3600);
      if (error) {
        console.warn('[CertificationUploader] createSignedUrls failed:', error);
        return new Map<string, string>();
      }
      const map = new Map<string, string>();
      (data ?? []).forEach((entry) => {
        if (entry.signedUrl && entry.path) {
          map.set(entry.path, entry.signedUrl);
        }
      });
      return map;
    },
    enabled: storagePaths.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes (signed URLs are valid 60min; refresh well before expiry)
  });

  return (
    <div className={cn('space-y-3', className)}>
      {/* Uploaded files list */}
      {(mediaItems?.length ?? 0) > 0 && (
        <div className="space-y-2">
          {mediaItems!.map((item) => (
            <div key={item.id} className="flex items-center gap-2 p-2 rounded-md border bg-muted/30">
              {item.mime_type.startsWith('image/') ? (
                <div className="h-10 w-10 rounded overflow-hidden bg-muted flex-shrink-0">
                  <img
                    src={signedUrlMap?.get(item.storage_path)}
                    alt="Certificate"
                    className="h-full w-full object-cover"
                  />
                </div>
              ) : (
                <div className="h-10 w-10 rounded bg-muted flex items-center justify-center flex-shrink-0">
                  <FileText className="h-5 w-5 text-muted-foreground" />
                </div>
              )}
              <span className="text-sm text-foreground truncate flex-1">
                {item.mime_type === 'application/pdf' ? 'PDF Document' : 'Image'}
                {item.file_size ? ` (${(item.file_size / 1024).toFixed(0)} KB)` : ''}
              </span>
              {!disabled && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 flex-shrink-0"
                  onClick={() => handleRemove(item.id, item.storage_path)}
                  disabled={deleteMedia.isPending}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Upload buttons */}
      {!disabled && (
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="flex-1"
            onClick={() => cameraInputRef.current?.click()}
            disabled={isUploading}
          >
            {isUploading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Camera className="h-3.5 w-3.5 mr-1.5" />}
            Camera
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="flex-1"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
          >
            <ImagePlus className="h-3.5 w-3.5 mr-1.5" />
            File
          </Button>
        </div>
      )}

      {/* Hidden inputs */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,application/pdf"
        onChange={handleFileChange}
        className="hidden"
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileChange}
        className="hidden"
      />
    </div>
  );
}
